/* Small device UI pieces: status chip, claim modal, edit modal. */
import { useState } from "react";
import type { Device, DeviceInput } from "../../../data/types";
import { ApiError } from "../../../data/provider";
import { useClaimDevice, useUpdateDevice } from "../../../data/hooks";
import { Alert, Button, Chip, Modal, SelectField, TextField, errMsg, useToast } from "../../../ui";
import { LOOKUP, useI18n } from "../../../i18n";
import { ago } from "../../../lib/format";
import { DEVICE_STATUS, TIMEZONES } from "./labels";

export function DeviceStatus({ d, now }: { d: Pick<Device, "status" | "lastSeen" | "id">; now: string }) {
  const { L, lang } = useI18n();
  const [en, ar] = DEVICE_STATUS[d.status];
  return (
    <span className="stack" style={{ gap: 2 }}>
      <span><Chip status={d.status} testId={"status-" + d.id}>{L(en, ar)}</Chip></span>
      {d.status !== "online" && d.lastSeen && <span className="xs muted">{L("last seen ", "آخر ظهور ")}{ago(d.lastSeen, now, lang)}</span>}
    </span>
  );
}

const branchOptions = (branch: (k: string) => string, L: (a: string, b: string) => string): [string, string][] =>
  [["", L("No branch", "بدون فرع")], ...Object.keys(LOOKUP.BR).map((k): [string, string] => [k, branch(k)])];

export function ClaimModal({ device, onClose, onClaimed }: { device: Device; onClose: () => void; onClaimed?: (d: Device) => void }) {
  const { L, branch, t } = useI18n();
  const toast = useToast();
  const claim = useClaimDevice();
  const [name, setName] = useState("");
  const [br, setBr] = useState(Object.keys(LOOKUP.BR)[0] ?? "");
  const [err, setErr] = useState("");
  const submit = () => {
    if (!name.trim()) { setErr(L("Give the device a name", "أدخل اسمًا للجهاز")); return; }
    claim.mutate({ id: device.id, name: name.trim(), branch: br }, {
      onSuccess: (d) => { toast(L("Device claimed — punches will now be recorded", "تم اعتماد الجهاز — سيتم تسجيل البصمات الآن")); onClaimed?.(d); onClose(); },
      onError: (e) => { if (e instanceof ApiError && e.fields?.name) setErr(e.fields.name); else toast(errMsg(e), "bad"); },
    });
  };
  return (
    <Modal title={L("Claim device", "اعتماد الجهاز")} onClose={onClose} testId="claim-modal"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={submit} loading={claim.isPending} data-testid="claim-ok">{L("Claim device", "اعتماد الجهاز")}</Button></>}>
      <div className="stack">
        <Alert tone="info" tint title={L(`Serial ${device.serial}`, `الرقم التسلسلي ${device.serial}`)}
          desc={L("Only claim devices you recognise. Check the serial matches the sticker or Menu → System Info.", "اعتمد فقط الأجهزة التي تعرفها. تأكد من مطابقة الرقم للملصق أو القائمة ← معلومات النظام.")} />
        <TextField label={L("Device name", "اسم الجهاز")} req value={name} onChange={(v) => { setName(v); setErr(""); }} err={err} placeholder={L("e.g. Jeddah HQ · Main entrance", "مثال: مقر جدة · المدخل الرئيسي")} testId="claim-name" />
        <SelectField label={t("Branch")} value={br} onChange={setBr} options={branchOptions(branch, L)} testId="claim-branch" />
      </div>
    </Modal>
  );
}

export function EditDeviceModal({ device, onClose }: { device: Device; onClose: () => void }) {
  const { L, branch, t } = useI18n();
  const toast = useToast();
  const upd = useUpdateDevice();
  const [f, setF] = useState({ name: device.name, branch: device.branch, timezone: device.timezone, directionMode: device.directionMode, baseUrl: device.baseUrl, username: device.username, secret: "", pollIntervalSec: String(device.pollIntervalSec) });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const set = (k: keyof typeof f, v: string) => { setF((x) => ({ ...x, [k]: v })); setErrs((e) => ({ ...e, [k]: "" })); };
  const save = () => {
    if (!f.name.trim()) { setErrs({ name: L("Give the device a name", "أدخل اسمًا للجهاز") }); return; }
    const patch: Partial<DeviceInput> = { name: f.name.trim(), branch: f.branch, timezone: f.timezone, directionMode: f.directionMode as Device["directionMode"] };
    if (device.protocol === "poll") Object.assign(patch, { baseUrl: f.baseUrl.trim(), username: f.username.trim(), pollIntervalSec: Number(f.pollIntervalSec), ...(f.secret ? { secret: f.secret } : {}) });
    upd.mutate({ id: device.id, patch }, {
      onSuccess: () => { toast(L("Device updated", "تم تحديث الجهاز")); onClose(); },
      onError: (e) => { if (e instanceof ApiError && e.fields) setErrs(e.fields); else toast(errMsg(e), "bad"); },
    });
  };
  return (
    <Modal title={L("Edit device", "تعديل الجهاز")} onClose={onClose} testId="edit-device"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={save} loading={upd.isPending} data-testid="edit-save">{t("Save")}</Button></>}>
      <div className="stack">
        <TextField label={L("Device name", "اسم الجهاز")} req value={f.name} onChange={(v) => set("name", v)} err={errs.name} testId="edit-name" />
        <div className="form2">
          <SelectField label={t("Branch")} value={f.branch} onChange={(v) => set("branch", v)} options={branchOptions(branch, L)} testId="edit-branch" />
          <SelectField label={L("Time zone", "المنطقة الزمنية")} value={f.timezone} onChange={(v) => set("timezone", v)} options={TIMEZONES.includes(f.timezone) ? TIMEZONES : [f.timezone, ...TIMEZONES]} err={errs.timezone} testId="edit-timezone" />
        </div>
        <SelectField label={L("In / out detection", "تحديد الدخول والخروج")} value={f.directionMode} onChange={(v) => set("directionMode", v)} testId="edit-direction"
          options={[["device", L("As sent by the device", "كما يرسلها الجهاز")], ["first-in-last-out", L("First punch in, last punch out", "أول بصمة دخول وآخر بصمة خروج")], ["alternate", L("Alternate in / out", "تبديل دخول / خروج")]]} />
        {device.protocol === "poll" && (
          <>
            <TextField label={L("Server URL", "عنوان الخادم")} value={f.baseUrl} onChange={(v) => set("baseUrl", v)} err={errs.baseUrl} dir="ltr" testId="edit-baseurl" />
            <div className="form2">
              <TextField label={L("Username", "اسم المستخدم")} value={f.username} onChange={(v) => set("username", v)} dir="ltr" testId="edit-username" />
              <TextField label={L("New password / API key", "كلمة مرور / مفتاح جديد")} type="password" value={f.secret} onChange={(v) => set("secret", v)} dir="ltr" testId="edit-secret"
                hint={device.hasSecret ? L("Leave empty to keep the stored one", "اتركه فارغًا للإبقاء على المحفوظ") : L("Stored encrypted", "يُحفظ مشفّرًا")} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
